import React, { useState, useRef } from 'react';
import { 
  Upload, X, Play, Save, AlertTriangle, CheckCircle,
  Tag, Link as LinkIcon, Image as ImageIcon, Video
} from 'lucide-react';
import { useSupabase } from '../../contexts/SupabaseContext';
import { supabase } from '../../lib/supabase';

interface VideoUploadProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const VideoUpload: React.FC<VideoUploadProps> = ({ isOpen, onClose, onSuccess }) => {
  const { user } = useSupabase();
  const [formData, setFormData] = useState({
    title: '',
    tags: [''],
    product_links: [{ name: '', url: '' }]
  });
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [supplierId, setSupplierId] = useState<string | null>(null);
  
  const videoInputRef = useRef<HTMLInputElement>(null);
  const thumbnailInputRef = useRef<HTMLInputElement>(null);

  // Get supplier ID for current user
  React.useEffect(() => {
    const getSupplierInfo = async () => {
      if (!user) return;
      
      try {
        const { data, error } = await supabase
          .from('suppliers')
          .select('id')
          .eq('user_id', user.id)
          .single();
        
        if (error) throw error;
        
        if (data) {
          setSupplierId(data.id);
        }
      } catch (err) {
        console.error('Error fetching supplier info:', err);
        setError('You must be a verified supplier to upload videos.');
      }
    };
    
    if (isOpen) {
      getSupplierInfo();
    }
  }, [user, isOpen]);

  const handleVideoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('video/')) {
        setError('Please select a valid video file');
        return;
      }
      
      // Validate file size (max 100MB)
      if (file.size > 100 * 1024 * 1024) {
        setError('Video file must be less than 100MB');
        return;
      }
      
      setVideoFile(file);
      const url = URL.createObjectURL(file);
      setVideoPreview(url);
      setError(null);
    }
  };

  const handleThumbnailUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file for thumbnail');
        return;
      }
      
      setThumbnailFile(file);
      const url = URL.createObjectURL(file);
      setThumbnailPreview(url);
      setError(null);
    }
  };

  const handleTagChange = (index: number, value: string) => {
    const updatedTags = [...formData.tags];
    updatedTags[index] = value;
    setFormData(prev => ({ ...prev, tags: updatedTags }));
  };

  const addTag = () => {
    setFormData(prev => ({
      ...prev,
      tags: [...prev.tags, '']
    }));
  };

  const removeTag = (index: number) => {
    if (formData.tags.length > 1) {
      const updatedTags = [...formData.tags];
      updatedTags.splice(index, 1);
      setFormData(prev => ({ ...prev, tags: updatedTags }));
    }
  };

  const handleProductLinkChange = (index: number, field: 'name' | 'url', value: string) => {
    const updatedLinks = [...formData.product_links];
    updatedLinks[index][field] = value;
    setFormData(prev => ({ ...prev, product_links: updatedLinks }));
  };

  const addProductLink = () => {
    setFormData(prev => ({
      ...prev,
      product_links: [...prev.product_links, { name: '', url: '' }]
    }));
  };

  const removeProductLink = (index: number) => {
    if (formData.product_links.length > 1) {
      const updatedLinks = [...formData.product_links];
      updatedLinks.splice(index, 1);
      setFormData(prev => ({ ...prev, product_links: updatedLinks }));
    }
  };

  const uploadFileToStorage = async (file: File, bucket: string, path: string): Promise<string> => {
    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(path, file, {
        cacheControl: '3600',
        upsert: false,
        contentType: file.type
      });
    
    if (error) throw error;
    
    const { data: { publicUrl } } = supabase.storage
      .from(bucket)
      .getPublicUrl(data.path);
    
    return publicUrl;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user || !supplierId) {
      setError('You must be a verified supplier to upload videos');
      return;
    }
    
    if (!videoFile) {
      setError('Please select a video file');
      return;
    }
    
    if (!formData.title.trim()) {
      setError('Please enter a video title');
      return;
    }
    
    try {
      setUploading(true);
      setError(null);
      
      // Upload video file
      const videoPath = `videos/${supplierId}/${Date.now()}_${videoFile.name}`;
      const videoUrl = await uploadFileToStorage(videoFile, 'videos', videoPath);
      
      // Upload thumbnail if provided
      let thumbnailUrl = null;
      if (thumbnailFile) {
        const thumbnailPath = `video-thumbnails/${supplierId}/${Date.now()}_${thumbnailFile.name}`;
        thumbnailUrl = await uploadFileToStorage(thumbnailFile, 'video-thumbnails', thumbnailPath);
      }
      
      // Prepare data for database
      const videoData = {
        seller_id: supplierId,
        title: formData.title.trim(),
        video_url: videoUrl,
        thumbnail_url: thumbnailUrl,
        tags: formData.tags.filter(tag => tag.trim() !== ''),
        product_links: formData.product_links.filter(link => link.name.trim() !== '' && link.url.trim() !== ''),
        status: 'pending' // Videos need approval
      };
      
      // Insert video record
      const { error: insertError } = await supabase
        .from('videos')
        .insert(videoData);
      
      if (insertError) throw insertError;
      
      // Reset form and close modal
      setFormData({
        title: '',
        tags: [''],
        product_links: [{ name: '', url: '' }]
      });
      setVideoFile(null);
      setThumbnailFile(null);
      setVideoPreview(null);
      setThumbnailPreview(null);
      
      onSuccess();
      onClose();
      
    } catch (err) {
      console.error('Error uploading video:', err);
      setError('Failed to upload video. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Upload Video</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center">
              <AlertTriangle className="h-5 w-5 mr-2" />
              <span>{error}</span>
            </div>
          )}
          
          {/* Video Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Video File *
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
              {videoPreview ? (
                <div className="relative">
                  <video 
                    src={videoPreview} 
                    controls 
                    className="w-full h-48 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setVideoFile(null);
                      setVideoPreview(null);
                    }}
                    className="absolute top-2 right-2 p-1 bg-red-100 rounded-full text-red-600 hover:bg-red-200"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="text-center">
                  <Video className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <input
                    type="file"
                    ref={videoInputRef}
                    onChange={handleVideoUpload}
                    accept="video/*"
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => videoInputRef.current?.click()}
                    className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors"
                  >
                    Select Video
                  </button>
                  <p className="text-sm text-gray-500 mt-2">
                    MP4, WebM, or MOV (max 100MB)
                  </p>
                </div>
              )}
            </div>
          </div>
          
          {/* Thumbnail Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Thumbnail (Optional)
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
              {thumbnailPreview ? (
                <div className="relative">
                  <img 
                    src={thumbnailPreview} 
                    alt="Thumbnail preview" 
                    className="w-full h-32 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setThumbnailFile(null);
                      setThumbnailPreview(null);
                    }}
                    className="absolute top-2 right-2 p-1 bg-red-100 rounded-full text-red-600 hover:bg-red-200"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="text-center">
                  <ImageIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <input
                    type="file"
                    ref={thumbnailInputRef}
                    onChange={handleThumbnailUpload}
                    accept="image/*"
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => thumbnailInputRef.current?.click()}
                    className="text-orange-600 hover:text-orange-700 text-sm font-medium"
                  >
                    Upload Thumbnail
                  </button>
                  <p className="text-xs text-gray-500 mt-1">
                    JPG, PNG (recommended: 16:9 aspect ratio)
                  </p>
                </div>
              )}
            </div>
          </div>
          
          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
              Video Title *
            </label>
            <input
              id="title"
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="Enter video title"
            />
          </div>
          
          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tags
            </label>
            <div className="space-y-2">
              {formData.tags.map((tag, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <Tag className="h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={tag}
                    onChange={(e) => handleTagChange(index, e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Enter tag"
                  />
                  {formData.tags.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeTag(index)}
                      className="p-2 text-red-600 hover:text-red-700"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addTag}
                className="text-orange-600 hover:text-orange-700 text-sm font-medium"
              >
                + Add Tag
              </button>
            </div>
          </div>
          
          {/* Product Links */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Featured Products (Optional)
            </label>
            <div className="space-y-3">
              {formData.product_links.map((link, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center space-x-2 mb-2">
                    <LinkIcon className="h-4 w-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-700">Product {index + 1}</span>
                    {formData.product_links.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeProductLink(index)}
                        className="p-1 text-red-600 hover:text-red-700"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <input
                      type="text"
                      value={link.name}
                      onChange={(e) => handleProductLinkChange(index, 'name', e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="Product name"
                    />
                    <input
                      type="url"
                      value={link.url}
                      onChange={(e) => handleProductLinkChange(index, 'url', e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="Product URL"
                    />
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={addProductLink}
                className="text-orange-600 hover:text-orange-700 text-sm font-medium"
              >
                + Add Product Link
              </button>
            </div>
          </div>
          
          {/* Submit Button */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading || !supplierId}
              className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Uploading...</span>
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  <span>Upload Video</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VideoUpload;